/**
 * @file AssignablePersonPicker.tsx
 * @brief Assign a company person to a manual meeting to-do.
 *
 * @details
 * Uses the shared `PickerSheet` (searchable rail) — the same control as
 * EmployeePicker / ScheduleEmployeePicker / UserPicker. A plain Select
 * cannot scale past a few dozen people; companies with 100+ employees
 * need search. Data comes from `useAssignablePeople` (full company roster,
 * not meeting speakers), matching mobile `AddTodoRow` + VZPicker.
 *
 * @dependencies
 * - components/ui/PickerSheet, Avatar
 * - features/company-meetings/api (useAssignablePeople)
 *
 * @references
 * - FRONTEND/.../AddTodoRow.tsx (mobile twin)
 * - features/company-sessions/components/EmployeePicker.tsx
 *
 * B48 (02-11-2026): with `onAddSomeone`, a search that is typed shows "Add
 * someone: <typed name>" above the rows; it opens Name + Email (both
 * required, AddPersonForm) for a person who is neither a speaker nor in the
 * company.
 *
 * @author DES
 * @date 05-08-2026
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, User as UserIcon, UserPlus, UserRound, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { useAssignablePeople } from "../api";
import { AddPersonForm, type NewPerson } from "@/features/employee-boardroom/AddPersonForm";

/** Sentinel for "nobody yet" — a valid pick, not an empty value. */
export const ASSIGNABLE_UNASSIGNED = "__unassigned__";

/**
 * A row offered before the company list (28-09-2026, reassigning an AI
 * to-do): "Team", or someone who spoke in the meeting but is not in the
 * company. Its `id` is what `onChange` receives.
 */
export type AssignableExtraItem = {
  id: string;
  label: string;
  subtitle?: string;
  avatarUrl?: string | null;
  isTeam?: boolean;
};

interface AssignablePersonPickerProps {
  value?: string | null;
  onChange: (userId: string | null) => void;
  extraItems?: AssignableExtraItem[];
  /** Replaces the default "anyone in your company" subtitle. */
  subtitle?: string;
  /** B48: offer "Add someone: <typed name>" (Name + Email). */
  onAddSomeone?: (p: NewPerson) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function AssignablePersonPicker({
  value,
  onChange,
  placeholder = "Unassigned",
  disabled,
  className,
  extraItems,
  subtitle = "Anyone in your company — they need not have been in the meeting.",
  onAddSomeone,
}: AssignablePersonPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const people = useAssignablePeople(true);

  const selectedExtra = useMemo(
    () => (extraItems ?? []).find((x) => x.id === value) ?? null,
    [extraItems, value],
  );
  const selected = useMemo(
    () =>
      selectedExtra
        ? {
            id: selectedExtra.id,
            name: selectedExtra.label,
            avatarUrl: selectedExtra.avatarUrl ?? null,
            email: undefined as string | undefined,
          }
        : ((people.data ?? []).find((p) => p.id === value) ?? null),
    [people.data, value, selectedExtra],
  );

  const items: SearchableMenuItem[] = useMemo(() => {
    const unassigned: SearchableMenuItem = {
      value: ASSIGNABLE_UNASSIGNED,
      label: "Unassigned",
      description: "No one yet, can be claimed later",
      icon: (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <UserRound className="h-3.5 w-3.5" aria-hidden />
        </span>
      ),
    };
    const team: SearchableMenuItem[] = [];
    const extras: SearchableMenuItem[] = [];
    for (const x of extraItems ?? []) {
      const row: SearchableMenuItem = {
        value: x.id,
        label: x.label,
        description: x.subtitle,
        icon: x.isTeam ? (
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background">
            <Users className="h-3.5 w-3.5" aria-hidden />
          </span>
        ) : (
          <Avatar
            src={x.avatarUrl ?? null}
            initials={getInitials(x.label, null, null)}
            alt=""
            size="sm"
          />
        ),
      };
      (x.isTeam ? team : extras).push(row);
    }
    const people_: SearchableMenuItem[] = (people.data ?? []).map((p) => ({
      value: p.id,
      label: p.name || p.email || p.id,
      description: p.name && p.email ? p.email : undefined,
      keywords: p.email,
      icon: (
        <Avatar src={p.avatarUrl} initials={getInitials(p.name, null, p.email)} alt="" size="sm" />
      ),
    }));
    // Team first, then Unassigned, the extra rows, then everyone (as before).
    return [...team, unassigned, ...extras, ...people_];
  }, [people.data, extraItems]);

  const triggerLabel = selected ? selected.name || selected.email || placeholder : placeholder;

  // Filtered here (the list is controlled so the typed text can name the new
  // person), with the same match the list uses on its own.
  const q = query.trim().toLowerCase();
  const shownItems = adding
    ? []
    : q
      ? items.filter((it) =>
          [it.label, it.description, it.keywords].filter(Boolean).join(" ").toLowerCase().includes(q),
        )
      : items;
  const typedName = query.trim();
  const addHeader = !onAddSomeone || !typedName ? null : adding ? (
    <div className="px-1 pb-2">
      <p className="px-1 pb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Add someone
      </p>
      <AddPersonForm
        initialName={typedName.includes("@") ? "" : typedName}
        submitLabel="Add"
        onCancel={() => setAdding(false)}
        onAdd={(p) => {
          onAddSomeone(p);
          setAdding(false);
          setQuery("");
          setOpen(false);
        }}
      />
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setAdding(true)}
      className="mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
        <UserPlus className="h-3.5 w-3.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 truncate">
        Add someone: <span className="font-semibold">{typedName}</span>
      </span>
    </button>
  );

  return (
    // A dropdown under the button, searchable (DES 30-09-2026: "ab user
    // picker aisa banao"), in place of the side sheet.
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setAdding(false);
          setQuery("");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled || people.isLoading}
          aria-label="Assign to"
          className={cn(
            "flex h-11 w-full items-center justify-between gap-2 rounded-full border border-input bg-field px-4 text-sm transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selectedExtra?.isTeam ? (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                <Users className="h-3.5 w-3.5" />
              </span>
            ) : selected ? (
              <Avatar
                src={selected.avatarUrl}
                initials={getInitials(selected.name, null, selected.email)}
                alt={triggerLabel}
                size="sm"
              />
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                <UserIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72 p-1.5"
      >
        <SearchableMenuList
          title="Assign to"
          searchPlaceholder="Search people"
          items={shownItems}
          query={query}
          onQueryChange={(v) => {
            setQuery(v);
            setAdding(false);
          }}
          header={addHeader}
          loading={people.isLoading}
          noMatchText="No people match that search."
          selected={[value ?? ASSIGNABLE_UNASSIGNED]}
          onSelect={(id) => {
            onChange(id === ASSIGNABLE_UNASSIGNED ? null : id);
            setOpen(false);
          }}
        />
        <p className="px-2 pb-1 pt-1.5 text-[11px] leading-4 text-muted-foreground">{subtitle}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
